<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useInventoryStore } from '../stores/inventory'
import type { Product } from '../types'
import { formatCurrency } from '../utils/format'
import { UiModal, UiButton, UiInput, UiSelect, UiCard, UiAlert, UiLoader, UiBadge } from '../components/ui'

const inventoryStore = useInventoryStore()

const products = computed(() => inventoryStore.products)
const isLoading = computed(() => inventoryStore.isLoading)
const error = computed(() => inventoryStore.error)

const filters = ref({ name: '', type: '', status: '', priceMin: '', priceMax: '' })

const showModal = ref(false)
const isEditing = ref(false)
const form = ref<Partial<Product>>({
  name: '',
  description: '',
  price: 0,
  stock: 0,
  type: 'frame',
})
const saving = ref(false)
const deleteConfirmId = ref<string | null>(null)

const productTypeOptions = [
  { value: 'frame', label: 'Armação' },
  { value: 'lens', label: 'Lente' },
  { value: 'contact-lens', label: 'Lente de Contato' },
]

const hasActiveFilters = computed(() =>
  Object.values(filters.value).some(v => v !== '')
)

const filteredTotalPrice = computed(() =>
  filteredProducts.value.reduce((sum, p) => sum + p.price, 0)
)

const filteredProducts = computed(() => {
  let result = products.value
  if (filters.value.name) {
    const q = filters.value.name.toLowerCase()
    result = result.filter(p => p.name.toLowerCase().includes(q))
  }
  if (filters.value.type) {
    result = result.filter(p => p.type === filters.value.type)
  }
  if (filters.value.status === 'active') {
    result = result.filter(p => p.stock > 0)
  } else if (filters.value.status === 'inactive') {
    result = result.filter(p => p.stock <= 0)
  }
  if (filters.value.priceMin) {
    const min = parseFloat(filters.value.priceMin)
    if (!isNaN(min)) result = result.filter(p => p.price >= min)
  }
  if (filters.value.priceMax) {
    const max = parseFloat(filters.value.priceMax)
    if (!isNaN(max)) result = result.filter(p => p.price <= max)
  }
  return result
})

function clearFilters() {
  filters.value = { name: '', type: '', status: '', priceMin: '', priceMax: '' }
}

onMounted(() => {
  inventoryStore.fetchProducts()
})

function openNew() {
  isEditing.value = false
  form.value = { name: '', description: '', price: 0, stock: 0, type: 'frame' }
  showModal.value = true
}

function openEdit(product: Product) {
  isEditing.value = true
  form.value = { ...product }
  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

function typeLabel(type: string): string {
  const opt = productTypeOptions.find(o => o.value === type)
  return opt?.label || type
}

async function handleSave() {
  if (!form.value.name?.trim()) return
  saving.value = true
  if (isEditing.value && form.value.id) {
    await inventoryStore.updateProduct(form.value as Product)
  } else {
    await inventoryStore.addProduct(form.value as Omit<Product, 'id'>)
  }
  saving.value = false
  if (!inventoryStore.error) {
    closeModal()
  }
}

function confirmDelete(id: string) {
  deleteConfirmId.value = id
}

async function handleDelete() {
  if (!deleteConfirmId.value) return
  await inventoryStore.deleteProduct(deleteConfirmId.value)
  deleteConfirmId.value = null
}
</script>

<template>
  <div class="page-container">
    <div class="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-xl font-bold text-surface-800 dark:text-surface-100 sm:text-3xl">Produtos</h1>
        <p class="mt-1 text-xs text-surface-500 dark:text-surface-400 sm:text-sm">Gerencie seu inventário de produtos</p>
      </div>
      <UiButton @click="openNew">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        <span class="hidden sm:inline">Novo Produto</span>
        <span class="sm:hidden">Novo</span>
      </UiButton>
    </div>

    <UiAlert variant="error" :message="error || ''" dismissible @dismiss="inventoryStore.error = null" />

    <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <div>
        <label class="label">Nome</label>
        <input v-model="filters.name" type="text" placeholder="Buscar por nome..." class="input" />
      </div>
      <div>
        <label class="label">Categoria</label>
        <select v-model="filters.type" class="select">
          <option value="">Todas</option>
          <option value="frame">Armação</option>
          <option value="lens">Lente</option>
          <option value="contact-lens">Lente de Contato</option>
        </select>
      </div>
      <div>
        <label class="label">Status</label>
        <select v-model="filters.status" class="select">
          <option value="">Todos</option>
          <option value="active">Ativo (em estoque)</option>
          <option value="inactive">Inativo (sem estoque)</option>
        </select>
      </div>
      <div>
        <label class="label">Preço mín.</label>
        <input v-model="filters.priceMin" type="number" min="0" step="0.01" placeholder="0,00" class="input" />
      </div>
      <div>
        <label class="label">Preço máx.</label>
        <input v-model="filters.priceMax" type="number" min="0" step="0.01" placeholder="0,00" class="input" />
      </div>
      <div class="flex items-end sm:col-span-2 lg:col-span-5">
        <UiButton variant="secondary" size="sm" @click="clearFilters">
          Limpar Filtros
        </UiButton>
      </div>
    </div>

    <UiCard padding>
      <UiLoader v-if="isLoading && !products.length" text="Carregando produtos..." />

      <div v-else-if="filteredProducts.length === 0 && !error" class="flex flex-col items-center py-16 text-center">
        <div class="mb-4 rounded-full bg-surface-100 dark:bg-surface-800 p-4">
          <svg class="h-8 w-8 text-surface-400 dark:text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-surface-700 dark:text-surface-200">Nenhum produto cadastrado</h3>
        <p class="mt-1 text-sm text-surface-400 dark:text-surface-500">Clique em "Novo Produto" para começar</p>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Produto</th>
              <th class="hidden sm:table-cell">Tipo</th>
              <th>Preço</th>
              <th class="hidden sm:table-cell">Estoque</th>
              <th class="w-24 sm:w-40">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in filteredProducts" :key="product.id">
              <td>
                <div class="font-medium text-surface-800 dark:text-surface-100">{{ product.name }}</div>
                <div v-if="product.description" class="mt-0.5 text-xs text-surface-400 dark:text-surface-500">{{ product.description }}</div>
                <!-- Show type and stock on mobile -->
                <div class="mt-1 flex flex-wrap items-center gap-1 sm:hidden">
                  <UiBadge variant="brand" :label="typeLabel(product.type)" />
                  <UiBadge
                    :variant="product.stock <= 0 ? 'red' : product.stock <= 5 ? 'amber' : 'green'"
                    :label="`Est: ${product.stock}`"
                  />
                </div>
              </td>
              <td class="hidden sm:table-cell">
                <UiBadge variant="brand" :label="typeLabel(product.type)" />
              </td>
              <td class="font-mono font-medium text-surface-800 dark:text-surface-100">{{ formatCurrency(product.price) }}</td>
              <td class="hidden sm:table-cell">
                <UiBadge
                  :variant="product.stock <= 0 ? 'red' : product.stock <= 5 ? 'amber' : 'green'"
                  :label="String(product.stock)"
                />
              </td>
              <td>
                <div class="flex items-center gap-1 sm:gap-2">
                  <UiButton variant="ghost" size="sm" @click="openEdit(product)">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span class="hidden sm:inline">Editar</span>
                  </UiButton>
                  <UiButton variant="ghost" size="sm" @click="confirmDelete(product.id)">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span class="hidden sm:inline">Excluir</span>
                  </UiButton>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="hasActiveFilters">
            <tr>
              <td class="text-right text-sm font-semibold text-surface-600 dark:text-surface-300">Total filtrado:</td>
              <td class="hidden sm:table-cell"></td>
              <td class="font-mono font-bold text-purple-700 dark:text-purple-400">{{ formatCurrency(filteredTotalPrice) }}</td>
              <td class="hidden sm:table-cell"></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </UiCard>

    <UiModal :open="showModal" :title="isEditing ? 'Editar Produto' : 'Novo Produto'" @close="closeModal">
      <form @submit.prevent="handleSave" class="space-y-5">
        <UiInput v-model="form.name" label="Nome" placeholder="Nome do produto" required />
        <div>
          <label class="label">Descrição</label>
          <textarea
            v-model="form.description"
            placeholder="Descrição do produto (opcional)"
            class="input min-h-[80px] resize-none"
          />
        </div>
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div>
            <label class="label">Preço <span class="text-red-500">*</span></label>
            <input v-model.number="form.price" type="number" min="0" step="0.01" placeholder="0,00" required class="input" />
          </div>
          <div>
            <label class="label">Estoque <span class="text-red-500">*</span></label>
            <input v-model.number="form.stock" type="number" min="0" placeholder="0" required class="input" />
          </div>
          <UiSelect
            v-model="form.type"
            label="Tipo"
            :options="productTypeOptions"
            placeholder="Selecione o tipo"
            required
          />
        </div>
      </form>
      <template #footer>
        <UiButton variant="secondary" @click="closeModal">Cancelar</UiButton>
        <UiButton variant="primary" :loading="saving" @click="handleSave">
          {{ isEditing ? 'Salvar Alterações' : 'Adicionar Produto' }}
        </UiButton>
      </template>
    </UiModal>

    <UiModal
      :open="!!deleteConfirmId"
      title="Excluir Produto"
      max-width="max-w-sm"
      @close="deleteConfirmId = null"
    >
      <p class="text-sm text-surface-600 dark:text-surface-300">
        Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.
      </p>
      <template #footer>
        <UiButton variant="secondary" @click="deleteConfirmId = null">Cancelar</UiButton>
        <UiButton variant="danger" :loading="isLoading" @click="handleDelete">Excluir</UiButton>
      </template>
    </UiModal>
  </div>
</template>
